// User Management Component - Only visible to Admin/Manager users
import React, { useState, useEffect } from 'react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import PermissionGate from '../PermissionGate/PermissionGate';
import './UserManagement.css';

// NOTE: Make Admin / Remove Admin actions were removed on purpose —
// Administrator group membership is managed manually in the Cognito console
// (Usage & Cost Tracking decision P8). The backend endpoints no longer exist.

const UserManagement = () => {
  const { permissions, loading: permissionsLoading } = useUserPermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (permissions?.canSeeUserManagement) {
      loadUsers();
    }
  }, [permissions]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      // Use the global function we created
      const userList = await window.getUserList();
      setUsers(userList.users || []);
    } catch (err) {
      setError(err.message);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (window.confirm(`Are you sure you want to delete user ${username}?`)) {
      try {
        await window.removeUser(username);
        alert(`User ${username} has been deleted`);
        loadUsers(); // Refresh the list
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  const handleToggleEnabled = async (user) => {
    const action = user.enabled ? 'disable' : 'enable';
    if (window.confirm(`Are you sure you want to ${action} user ${user.email || user.username}?`)) {
      try {
        await window.setUserEnabled(user.username, !user.enabled);
        loadUsers(); // Refresh the list
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  if (permissionsLoading) {
    return (
      <div className="user-management">
        <div className="user-management-header">
          <h1>User Management</h1>
        </div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!permissions?.canSeeUserManagement) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access user management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>User Management</h1>
        <button onClick={loadUsers} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh Users'}
        </button>
      </div>

      {error && (
        <div className="user-management-error-message">
          {error}
        </div>
      )}

      <div className="user-list">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Groups</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={index}>
                <td>{user.email}</td>
                <td>
                  <div className="groups">
                    {user.groups.map((group, idx) => (
                      <span key={idx} className={`group-badge ${group.toLowerCase()}`}>
                        {group}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className={`status ${user.status.toLowerCase()}`}>
                    {user.status}
                  </span>
                  {user.enabled === false && (
                    <span className="status disabled"> (disabled)</span>
                  )}
                </td>
                <td>
                  <div className="actions">
                    {/* Managers/Admins can disable or re-enable accounts */}
                    <PermissionGate permission="manage_users">
                      <button
                        onClick={() => handleToggleEnabled(user)}
                        className={user.enabled === false ? 'btn-assign-admin' : 'btn-remove-admin'}
                      >
                        {user.enabled === false ? 'Enable' : 'Disable'}
                      </button>
                    </PermissionGate>

                    {/* Only admins can delete users */}
                    <PermissionGate permission="delete_users">
                      <button
                        onClick={() => handleDeleteUser(user.username)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </PermissionGate>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;
